import {
  addDayOffset,
  dateFromString,
  dateRangesOverlap,
  DateString,
  dateStringFromDate,
} from '../../../common/src/date_string.ts';
import { ClientOrg, OrgType, SongList, SongListType } from '../../../common/src/model.ts';
import { hasInterval } from '../../../common/src/model_util.ts';
import { getSchoolTermDateRange, getWeeklyDateForOrganization } from '../../../common/src/org.ts';
import { getHymnsOfTheMonthForDate, getWeeklySongEntriesForDate } from '../../../common/src/repeating_rows.ts';
import { localStorageGet, LocalStorageKey } from './client_local_storage.ts';

// `writtenThrough` is the last Sunday the server had written the church down through when `songLists` downloaded
// (fetchPassedSundays), undefined when unknown
export function getWeeklySongLists(org: ClientOrg, songLists: SongList[], writtenThrough?: DateString): SongList[] {
  // how far the server has written the church's passed Sundays down, else the instant as well as the date, so last
  // Sunday goes on showing its repeating rows' fill until it is written down (getFillStartSunday, issue #4192)
  const now = new Date();
  const today = dateStringFromDate(now);
  let weeklySongLists: SongList[];
  if (org.type === OrgType.School) {
    const schoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
    const dateRange = getSchoolTermDateRange(schoolTerms, today);
    weeklySongLists = !dateRange
      ? []
      : songLists.filter((songList) => hasInterval(songList) && dateRangesOverlap(songList.interval, dateRange));
  } else {
    // the hymn of the month and the Same-all-month rows shown as Hymn of the Month (issue #4170)
    const hymnsOfTheMonth = getHymnsOfTheMonthForDate(org, today, songLists, { today, now, writtenThrough });

    // each Sunday's worship service, with a church's repeating rows' songs merged in; a Sunday with no worship
    // service whose repeating rows hold songs gets a synthesized one (no id), so the Church tab shows what the daily
    // singing plans sing (issue #4170). A church's Sunday with no song is a Sunday with no list, whatever is stored for
    // it: a blank is no song anywhere people look (issue #4190), so a list holding only blanks, or nothing, shows no
    // section on the Church tab and no tile on the Today page
    const thisSunday = getWeeklyDateForOrganization(org, today);
    const sundaySongLists = [thisSunday, addDayOffset(thisSunday, -7)].flatMap((sunday): SongList[] => {
      const worshipServices = songLists.filter(
        (songList) => songList.type === SongListType.WorshipService && songList.date === sunday,
      );
      if (org.type !== OrgType.Church) {
        return worshipServices;
      }
      const songs = getWeeklySongEntriesForDate(org, sunday, songLists, { today, now, writtenThrough });
      if (songs.length === 0) {
        return [];
      }
      if (worshipServices.length === 0) {
        return [{ type: SongListType.WorshipService, date: sunday, songs }];
      }
      return worshipServices.map((songList) => ({ ...songList, songs }));
    });
    weeklySongLists = [...hymnsOfTheMonth, ...sundaySongLists];
  }

  const oneWeekAgoDate = addDayOffset(today, -7);
  const twoWeeksOutDate = addDayOffset(today, 14);

  const pastEvents = songLists.filter((songList) => {
    return songList.type === SongListType.Event && songList.date < today && songList.date >= oneWeekAgoDate;
  });

  const todayEvents = songLists.filter((songList) => {
    return songList.type === SongListType.Event && songList.date === today;
  });

  const nearFutureEvents = songLists.filter((songList) => {
    return songList.type === SongListType.Event && songList.date > today && songList.date <= twoWeeksOutDate;
  });

  const allFutureEvents = songLists.filter((songList) => {
    return songList.type === SongListType.Event && dateFromString(songList.date) > dateFromString(today);
  });

  let futureEvents = nearFutureEvents;

  if (!nearFutureEvents.length && allFutureEvents.length > 0) {
    futureEvents = [allFutureEvents[0]!];
  }

  return [...weeklySongLists, ...todayEvents, ...futureEvents, ...pastEvents];
}
